/**
 * PropertyLocation Component
 * 
 * Displays property location on a greyscale OpenStreetMap
 */

'use client';

import dynamic from 'next/dynamic';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

export interface PropertyLocationProps {
  address?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  messages: {
    location: {
      title: string;
      subtitle: string;
      getDirections: string;
      nearby: string;
    };
  };
  className?: string;
}

// Dynamic import of Leaflet to avoid SSR issues
const MapComponent = dynamic(
  () => import('./PropertyLocationMap'),
  { 
    ssr: false,
    loading: () => (
      <div className="w-full h-[400px] rounded-xl bg-gray-100 animate-pulse flex items-center justify-center">
        <p className="text-[#717171]">Loading map...</p>
      </div>
    )
  }
);

export function PropertyLocation({
  address,
  city,
  country,
  latitude = 0,
  longitude = 0,
  messages,
  className = ''
}: PropertyLocationProps) {
  // Construct full address
  const fullAddress = [address, city, country].filter(Boolean).join(', ');

  const handleGetDirections = () => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress || `${latitude},${longitude}`)}`;
    window.open(directionsUrl, '_blank');
  };

  return (
    <div className={`font-inter ${className}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 bg-gradient-to-b from-[#BBA339] to-[#E4CE4A] rounded-full"></div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#222222] mb-1 font-inter">
            {messages.location.title}
          </h2>
          {messages.location.subtitle && (
            <p className="text-[#717171]">{messages.location.subtitle}</p>
          )}
        </div>
      </div>

      {/* Address */}
      {fullAddress && (
        <div className="mb-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-[#BBA339] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-base text-[#222222] font-medium">{fullAddress}</p>
        </div>
      )}

      {/* Map Container */}
      <div className="relative w-full h-[400px] rounded-xl overflow-hidden mb-4 group">
        <MapComponent 
          latitude={latitude} 
          longitude={longitude}
          onGetDirections={handleGetDirections}
          getDirectionsLabel={messages.location.getDirections}
        />
      </div>

      {/* Get Directions Link (Mobile) */}
      <button
        onClick={handleGetDirections}
        className="w-full md:hidden px-6 py-3 bg-[#BBA339] text-white rounded-lg font-semibold hover:bg-[#243462] transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        {messages.location.getDirections}
      </button>
    </div>
  );
}

